import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@demand-iq/ui';
import AppSidebar from 'components/shared/AppSidebar';
import CompanyBanner from 'components/shared/CompanyBanner';
import GlobalSnackbar from 'components/shared/GlobalSnackbar';
import MobileBottomNav from 'components/shared/MobileBottomNav';
import { SnackbarProvider } from 'contexts/SnackbarContext';
import { UnsavedChangesProvider } from 'contexts/UnsavedChangesContext';
import useAceDwellCapture from 'hooks/useAceDwellCapture';
import useSpoofFromUrl from 'hooks/useSpoofFromUrl';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Toaster } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { isEditorRoute, isFullBleedRoute, isPresentationsWizardRoute } from '@/utils/layoutRouteUtils';
import { useUserInfo } from './UserProvider';

const SIDEBAR_STORAGE_KEY = 'sidebar-user-preference';

const useSidebarState = () => {
  const { pathname } = useLocation();
  const onEditor = isEditorRoute(pathname) || isPresentationsWizardRoute(pathname);

  const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
  const userPreference = stored === 'true' ? true : stored === 'false' ? false : null;

  const [open, setOpen] = useState(() => {
    if (onEditor) {
      return false;
    }
    return userPreference ?? true;
  });

  const prevOnEditor = useRef(onEditor);

  useEffect(() => {
    if (onEditor === prevOnEditor.current) {
      return;
    }
    prevOnEditor.current = onEditor;

    if (onEditor) {
      setOpen(false);
    } else {
      const pref = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      setOpen(pref === null ? true : pref === 'true');
    }
  }, [onEditor]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      setOpen(value);
      if (!onEditor) {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value));
      }
    },
    [onEditor]
  );

  return { open, onOpenChange: handleOpenChange, fullBleed: isFullBleedRoute(pathname) };
};

const HomeContent = (): JSX.Element => {
  const { user } = useUserInfo();
  const { open, onOpenChange, fullBleed } = useSidebarState();
  useAceDwellCapture();
  // Hold rendering while a ?companyId= deep link switches company context,
  // so the target page's queries never run against the wrong company.
  const isSpoofPending = useSpoofFromUrl();

  if (!user || isSpoofPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <SidebarProvider open={open} onOpenChange={onOpenChange}>
      <AppSidebar />
      <main className="flex h-svh flex-1 flex-col overflow-hidden md:h-auto">
        <CompanyBanner />
        <div className={cn('flex flex-1 flex-col overflow-auto', !fullBleed && 'px-4 py-6 sm:px-6 xl:px-10')}>
          <Outlet />
        </div>
        <MobileBottomNav />
      </main>
      <GlobalSnackbar />
      <Toaster />
    </SidebarProvider>
  );
};

const Home = (): JSX.Element => {
  return (
    <SnackbarProvider>
      <UnsavedChangesProvider>
        <HomeContent />
      </UnsavedChangesProvider>
    </SnackbarProvider>
  );
};

export default Home;
